import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  OnInit,
  OnDestroy,
  Output
} from "@angular/core";
import {NgForm} from "@angular/forms";
import {PANEL_DATA} from "../../../../commons/angular/services/panel.service";
import * as moment from 'moment-timezone';

interface AssignAvailabilityDialogData {
  fromTime?: number;
  toTime?: number;
  date?: string;
  zoneId?: string;
}

require('./assign-availability-dialog.scss');
@Component({
  standalone: false,
  selector: 'app-assign-availability-dialog',
  templateUrl: './assign-availability-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AssignAvailabilityDialogComponent implements OnInit, OnDestroy {

  @Output() onClose = new EventEmitter<any>();

  public fromTime: number | null = null;
  public toTime: number | null = null;
  public errorMessage: string = '';
  public dayStartTime: number = 0;
  public dayEndTime: number = 0;
  
  private previousDefaultTimezone: string | null = null;

  constructor(@Inject(PANEL_DATA) public data: AssignAvailabilityDialogData) {
  }

  ngOnInit() {
    const tz = this.data?.zoneId;
    
    // Store current default timezone and set visit plan timezone globally
    if (tz) {
      this.previousDefaultTimezone = moment.tz.guess();
      moment.tz.setDefault(tz);
    }
    
    // Get the base date in the correct timezone
    const baseDate = this.data?.date ? moment(this.data.date) : moment();
    
    // Set day boundaries (00:00 - 23:59 of the given date)
    this.dayStartTime = baseDate.clone().startOf('day').valueOf();
    this.dayEndTime = baseDate.clone().endOf('day').valueOf();
    
    // Set default times or use provided times
    if (this.data?.fromTime) {
      this.fromTime = this.data.fromTime;
    } else {
      // Set default time to 08:00 on the specified date
      const timeInZone = baseDate.clone().hours(8).minutes(0).seconds(0).milliseconds(0);
      this.fromTime = timeInZone.valueOf();
    }
    
    if (this.data?.toTime) {
      this.toTime = this.data.toTime;
    } else {
      // Set default time to 17:00 on the specified date
      const timeInZone = baseDate.clone().hours(17).minutes(0).seconds(0).milliseconds(0);
      this.toTime = timeInZone.valueOf();
    }
    
    // Validate initial times
    this.validateTimeRange();
  }

  ngOnDestroy() {
    // Restore previous default timezone when dialog closes
    if (this.previousDefaultTimezone) {
      moment.tz.setDefault(this.previousDefaultTimezone);
    }
  }

  onRangeChange(range: { from: number, to: number }) {
    this.fromTime = range.from;
    this.toTime = range.to;
    this.validateTimeRange();
  }

  onCancel() {
    this.onClose.emit();
  }

  onSubmit(form: NgForm) {
    if (form.invalid) {
      Object.values(form.controls).forEach(control => {
        control.markAsTouched();
      });
      return;
    }
    
    // Clear any previous errors
    this.errorMessage = '';
    
    // Validate time range
    if (!this.validateTimeRange()) {
      return;
    }
    
    this.onClose.emit({
      fromTime: this.fromTime,
      toTime: this.toTime
    });
  }

  private validateTimeRange(): boolean {
    if (!this.fromTime || !this.toTime) {
      this.errorMessage = 'Both start and end times are required';
      return false;
    }
    
    // Check if times are within the same day (0:00 - 23:59)
    if (!this.isTimeWithinDay(this.fromTime) || !this.isTimeWithinDay(this.toTime)) {
      this.errorMessage = 'Times must be within the same day (00:00 - 23:59)';
      return false;
    }
    
    // Check if end time is after start time
    if (this.toTime <= this.fromTime) {
      this.errorMessage = 'End time must be after start time';
      return false;
    }
    
    this.errorMessage = '';
    return true;
  }

  private isTimeWithinDay(timestamp: number): boolean {
    const time = moment(timestamp);
    const startOfDay = time.clone().startOf('day');
    const endOfDay = time.clone().endOf('day');
    return time.isBetween(startOfDay, endOfDay, null, '[]');
  }

}

